/**
 * IAB TCF/GPP bootstrap stubs, adapted from yoto-web's CMP bootstrap.
 *
 * This creates the locator iframes and queues early `__tcfapi` / `__gpp` calls
 * until InMobi Choice replaces the stubs with the real CMP APIs.
 */

import type { GppApi, TcfApi } from './cmpApis';

const TCF_LOCATOR_NAME = '__tcfapiLocator';
const GPP_LOCATOR_NAME = '__gppLocator';
const CMP_ID = 10;
const SUPPORTED_GPP_APIS = ['2:tcfeuv2', '6:uspv1', '7:usnatv1', '8:usca', '9:usvav1', '10:uscov1', '11:usutv1', '12:usctv1'];
let tcfMessageHandlerInstalled = false;
let gppMessageHandlerInstalled = false;

type TcfStub = TcfApi & { _kzStub?: true; queue?: unknown[][] };
type GppStub = GppApi & { _kzStub?: true; queue?: unknown[][]; events?: GppEvent[]; lastId?: number };

interface GppEvent {
  id: number;
  callback: GppCallbackLike | null;
  parameter: unknown;
}

type GppCallbackLike = (data: unknown, success: boolean) => void;

export function initTcfBoilerplate(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  installTcfStub();
  installGppStub();
}

function installTcfStub(): void {
  if (window.__tcfapi && !(window.__tcfapi as TcfStub)._kzStub) return;
  if (hasFrame(TCF_LOCATOR_NAME) && window.__tcfapi) return;

  const queue: unknown[][] = (window.__tcfapi as TcfStub | undefined)?.queue ?? [];
  addFrameWhenReady(TCF_LOCATOR_NAME);

  const stub = function (...args: unknown[]): unknown[] | undefined {
    if (args.length === 0) return queue;
    const [command, version, cb, parameter] = args;
    if (command === 'ping' && typeof cb === 'function') {
      cb({ cmpLoaded: false, cmpStatus: 'stub' }, true);
      return undefined;
    }
    if (command === 'init' && typeof parameter === 'object' && parameter !== null) {
      Object.assign(parameter, { tag_version: 'V3' });
    }
    queue.push(args);
    return undefined;
  } as TcfStub;
  stub._kzStub = true;
  stub.queue = queue;
  window.__tcfapi = stub;
  if (!tcfMessageHandlerInstalled) {
    tcfMessageHandlerInstalled = true;
    window.addEventListener('message', handleTcfPostMessage, false);
  }
}

function installGppStub(): void {
  const w = window as Window & {
    __gpp_addFrame?: (name: string) => void;
    __gpp_msghandler?: (event: MessageEvent) => void;
  };
  if (window.__gpp) return;

  addFrameWhenReady(GPP_LOCATOR_NAME);
  const existing = window.__gpp as GppStub | undefined;
  const stub = function (command: string, cb?: GppCallbackLike | null, parameter?: unknown): unknown[] | GppEvent[] | undefined {
    stub.queue ??= [];
    stub.events ??= [];

    if (!command || command === 'queue') return stub.queue;
    if (command === 'events') return stub.events;
    if (command === 'ping') {
      cb?.(makeGppPingData(), true);
      return undefined;
    }
    if (command === 'addEventListener') {
      stub.lastId = (stub.lastId ?? 0) + 1;
      const listenerId = stub.lastId;
      stub.events.push({ id: listenerId, callback: cb ?? null, parameter });
      cb?.({ eventName: 'listenerRegistered', listenerId, data: true, pingData: makeGppPingData() }, true);
      return undefined;
    }
    if (command === 'removeEventListener') {
      const before = stub.events.length;
      stub.events = stub.events.filter((event) => event.id !== parameter);
      cb?.({ eventName: 'listenerRemoved', listenerId: parameter, data: stub.events.length !== before, pingData: makeGppPingData() }, true);
      return undefined;
    }
    if (command === 'hasSection') {
      cb?.(false, true);
      return undefined;
    }
    if (command === 'getSection' || command === 'getField') {
      cb?.(null, true);
      return undefined;
    }
    stub.queue.push([command, cb, parameter]);
    return undefined;
  } as GppStub;
  stub._kzStub = true;
  stub.queue = existing?.queue ?? [];
  stub.events = existing?.events ?? [];
  window.__gpp = stub;

  w.__gpp_addFrame = addFrameWhenReady;
  w.__gpp_msghandler = handleGppPostMessage;
  if (!gppMessageHandlerInstalled) {
    gppMessageHandlerInstalled = true;
    window.addEventListener('message', handleGppPostMessage, false);
  }
}

function addFrameWhenReady(name: string): void {
  if (hasFrame(name)) return;
  if (!document.body) {
    window.setTimeout(() => addFrameWhenReady(name), 5);
    return;
  }
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'display:none';
  iframe.name = name;
  document.body.appendChild(iframe);
}

function hasFrame(name: string): boolean {
  try {
    return !!(window.frames as unknown as Record<string, Window>)[name];
  } catch {
    return false;
  }
}

function handleTcfPostMessage(event: MessageEvent): void {
  const payload = readMessagePayload(event.data, '__tcfapiCall') as
    | { command: string; version: number; parameter?: unknown; callId: string }
    | null;
  if (!payload || !window.__tcfapi) return;

  window.__tcfapi(payload.command as 'addEventListener', payload.version, (returnValue, success) => {
    const response = {
      __tcfapiReturn: {
        returnValue,
        success,
        callId: payload.callId,
      },
    };
    (event.source as Window | null)?.postMessage(typeof event.data === 'string' ? JSON.stringify(response) : response, '*');
  }, payload.parameter as number);
}

function handleGppPostMessage(event: MessageEvent): void {
  const payload = readMessagePayload(event.data, '__gppCall') as
    | { command: string; parameter?: unknown; version?: string; callId: string }
    | null;
  if (!payload || !window.__gpp) return;

  window.__gpp(payload.command, (returnValue, success) => {
    const response = {
      __gppReturn: {
        returnValue,
        success,
        callId: payload.callId,
      },
    };
    (event.source as Window | null)?.postMessage(typeof event.data === 'string' ? JSON.stringify(response) : response, '*');
  }, payload.parameter);
}

function readMessagePayload(data: unknown, key: string): unknown | null {
  let parsed: unknown = data;
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object' || !(key in parsed)) return null;
  return (parsed as Record<string, unknown>)[key];
}

function makeGppPingData(): Record<string, unknown> {
  return {
    gppVersion: '1.1',
    cmpStatus: 'stub',
    cmpDisplayStatus: 'hidden',
    signalStatus: 'not ready',
    supportedAPIs: SUPPORTED_GPP_APIS,
    cmpId: CMP_ID,
    sectionList: [],
    applicableSections: [-1],
    gppString: '',
    parsedSections: {},
  };
}
